import { useEffect, useState, useRef } from 'react';
import { Mail, CheckCircle2, RefreshCw } from 'lucide-react';
import { api, json } from '../api';
import { usePlatform } from '../store';
import './email-verification.css';
type Status = { email: string; verified: boolean; ready: boolean; retryAfter: number };
export function EmailVerification({ compact = false }: { compact?: boolean }) {
  const { data, refresh } = usePlatform();
  const [status, setStatus] = useState<Status | null>(null),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [email, setEmail] = useState(''),
    [editing, setEditing] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const identity = useRef(data?.user?.id);
  identity.current = data?.user?.id;
  async function load() {
    const userId = identity.current;
    try {
      const value = await api<Status>('/email-verification');
      if (identity.current !== userId) return;
      setStatus(value);
      setRemaining(value.retryAfter);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => {
    setStatus(null);
    setMessage('');
    setEditing(false);
    if (!data?.user) return;
    let current = true;
    const update = () => {
      if (!document.hidden && current) void load();
    };
    update();
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      current = false;
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [data?.user?.id]);
  useEffect(() => {
    const t = setInterval(() => setRemaining((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  if (!data?.user) return null;
  async function send() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (!status?.email || editing) {
        await api('/email-verification/email', json('POST', { email: email.trim() }));
        setEditing(false);
        await refresh();
      }
      await api('/email-verification/send', json('POST', {}));
      setMessage('认证邮件已发送，请检查收件箱或垃圾邮件。链接24小时内有效。');
      await load();
    } catch (e) {
      setError((e as Error).message);
      await load();
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className={
        'email-verification-card' +
        (compact ? ' is-compact' : '') +
        (status?.verified ? ' is-verified' : '')
      }
      aria-label="邮箱认证"
    >
      <div className="email-verification-title">
        {status?.verified ? <CheckCircle2 size={19} /> : <Mail size={19} />}
        <strong>邮箱认证</strong>
        <span>{status?.verified ? '已认证' : '待认证'}</span>
      </div>
      {status && (
        <>
          {(!compact || !status.email) && <p>{status.email || '添加邮箱，接收账号认证邮件。'}</p>}
          {!status.verified && (
            <>
              {!compact && <p className="email-verification-hint">点击邮件中的链接完成认证。</p>}
              {(!status.email || editing) && (
                <input
                  type="email"
                  aria-label="认证邮箱"
                  placeholder="填写邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
              <button
                className="button secondary"
                disabled={
                  busy ||
                  remaining > 0 ||
                  !status.ready ||
                  ((!status.email || editing) && !email.trim())
                }
                onClick={() => void send()}
              >
                <RefreshCw size={15} />
                {busy
                  ? '正在发送…'
                  : remaining > 0
                    ? `${remaining}秒后可重发`
                    : editing
                      ? '保存并发送认证邮件'
                      : '重发认证邮件'}
              </button>
              {status.email && (
                <button
                  className="email-change-link"
                  disabled={busy}
                  onClick={() => {
                    setEditing(!editing);
                    setEmail(status.email);
                    setError('');
                  }}
                >
                  {editing ? '取消修改' : '邮箱填错了？'}
                </button>
              )}
              {!status.ready && <small>邮件服务暂不可用，请稍后重试。</small>}
            </>
          )}
        </>
      )}
      {!status && !error && <p>正在检查认证状态…</p>}
      {message && <p role="status">{message}</p>}
      {error && (
        <p role="alert" className="email-verification-error">
          {error} {!status && <button onClick={() => void load()}>重新加载</button>}
        </p>
      )}
    </section>
  );
}
